import * as React from "react";

import { Link } from "react-router";

import * as S from "./Button.styles";
import { LayoutProps, Variant, Content } from "./Button.types";

const getCapitalized = (
  capitalized: boolean | undefined | string
): "true" | "false" => {
  if (typeof capitalized === "boolean") {
    return capitalized.toString() as "true" | "false";
  }
  if (capitalized === "true") return "true";

  return "false";
};

type OnlyFirst<First, Second> = First & {
  [K in keyof Omit<Second, keyof First>]?: never;
};

type MergeTypes<
  TypesArray extends any[],
  Result = unknown
> = TypesArray extends [infer Head, ...infer Rest]
  ? MergeTypes<Rest, Result & Head>
  : Result;

type OneOf<
  TypesArray extends any[],
  Result = never,
  AllProperties = MergeTypes<TypesArray>
> = TypesArray extends [infer Head, ...infer Rest]
  ? OneOf<Rest, Result | OnlyFirst<Head, AllProperties>, AllProperties>
  : Result;

export type ButtonProps = React.ComponentPropsWithRef<"button"> &
  LayoutProps & {
    analyticId?: string;
    as?: React.ElementType;
    htmlFor?: string;
  };

export type AnchorProps = React.ComponentPropsWithRef<"a"> &
  LayoutProps & {
    href: string;
    analyticId?: string;
  };

export type RouterLinkProps = React.ComponentPropsWithRef<typeof Link> &
  LayoutProps & {
    analyticId?: string;
  };

export type PolymorphicProps = OneOf<
  [ButtonProps, AnchorProps, RouterLinkProps]
>;

type PolymorphicButton = {
  (props: PolymorphicProps): React.JSX.Element;
  displayName?: string;
};

const isAnchor = (props: PolymorphicProps): props is AnchorProps =>
  !!props.href;

const isReactRouterLink = (props: PolymorphicProps): props is RouterLinkProps =>
  !!props.to;

export const Button = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  PolymorphicProps
>((props, ref) => {
  const defaults = {
    variant: props.variant || Variant.primary,
    content: props.content || Content.text,
    capitalized: getCapitalized(props.capitalized),
    type:
      (props as any).type &&
      ["submit", "reset", "button"].includes((props as any).type)
        ? (props as any).type
        : "button"
  };

  if (isReactRouterLink(props)) {
    const { analyticId, type: _type, ...restProps } = props as any;
    const linkDefaults = { ...defaults };
    delete linkDefaults.type;
    return (
      <S.Layout
        ref={ref as React.ForwardedRef<HTMLAnchorElement>}
        as={Link}
        {...restProps}
        {...linkDefaults}
        data-pendo-identifier={analyticId}
      />
    );
  }

  if (isAnchor(props)) {
    const { analyticId, type: _type, ...restProps } = props as any;
    const anchorDefaults = { ...defaults };
    delete anchorDefaults.type;
    return (
      <S.Layout
        ref={ref as React.ForwardedRef<HTMLAnchorElement>}
        as="a"
        target="_blank"
        rel="noopener"
        {...restProps}
        {...anchorDefaults}
        data-pendo-identifier={analyticId}
      />
    );
  }

  // Guards above eliminated RouterLinkProps and AnchorProps.
  const { analyticId, ...restProps } = props as ButtonProps;
  return (
    <S.Layout
      ref={ref as React.ForwardedRef<HTMLButtonElement>}
      {...(restProps as any)}
      {...defaults}
      data-pendo-identifier={analyticId}
    />
  );
}) as PolymorphicButton;

Button.displayName = "Button";
